import { MultipartFailureKind } from '@atlas/types'
import { logger } from '@/lib/logger'
import type { FileDTO } from '@/services/file.service'
import { fileMultipartService } from '@/services/file-multipart.service'
import { UploadRecoveryAction } from '../../upload-recovery'
import type { PreparedUploadInput, UploaderCallbacks, UploaderStrategyResult, UploadStrategy } from '../uploader'
import { MultipartUploadError, multipartErrorCode, multipartErrorDetails } from './multipart-errors'
import { finalizeSession } from './multipart-finishing'
import { PartProgress, planParts } from './multipart-parts'
import { PartUrlProvider } from './multipart-presign'
import { requestPresignWithBackoff } from './multipart-presign-retry'
import type { ResolvedMultipartSession } from './multipart-session-resolution'
import { MultipartFallbackSignal, releaseSessionFn, resolveSession } from './multipart-session-resolution'
import { forgetSession } from './multipart-session-storage'
import { MultipartUploadGoneError, transferParts } from './multipart-transfer'

const log = logger.child('multipart-uploader')

/** How many times a `MULTIPART_PART_SET_INVALID` answer may be repaired by re-uploading exactly what it names. */
const PART_SET_REPAIR_ROUNDS = 2

/**
 * The multipart transport: browser to S3, part by part, against a File the
 * caller already created.
 *
 * It owns slicing, demand-driven presigning, per-part retry, progress and
 * finalization for one upload, and nothing beyond that: it never creates a
 * File, never resolves `buildClassify`, never queues anything, and never calls
 * another strategy. When multipart cannot proceed but a single PUT safely can,
 * it returns that verdict for `UploaderService` to act on.
 */
export const multipartUploader: UploadStrategy = {
	execute: async (input: PreparedUploadInput, callbacks: UploaderCallbacks): Promise<UploaderStrategyResult> => {
		try {
			return { kind: 'completed', file: await runMultipartUpload(input, callbacks) }
		} catch (error) {
			if (error instanceof MultipartFallbackSignal) {
				// The internal reason is a diagnostic, never a user-facing message.
				log.info('Multipart handed the upload back to single PUT', { reason: error.reason })
				return { kind: 'fallback_to_single_put', reason: error.reason }
			}
			throw error
		}
	},
}

async function runMultipartUpload(input: PreparedUploadInput, callbacks: UploaderCallbacks): Promise<FileDTO> {
	const resolution = await resolveSession({ fileId: input.fileId, file: input.file, classify: input.classify })
	if (resolution.kind === 'confirmed') {
		// A previous attempt already reached the confirmation boundary; nothing is
		// transferred and the same final File resolves this upload.
		callbacks.onFileCreated(input.fileId)
		callbacks.onConfirming()
		forgetSession(input.file)
		return resolution.file
	}

	const session = resolution.session
	// Transport is ready: a session exists and parts can actually start moving.
	callbacks.onFileCreated(input.fileId)

	const progress = new PartProgress(session.declaredFileSize)
	for (const part of session.alreadyUploaded) progress.markCompleted(part.partNumber, part.size)
	callbacks.onProgress(progress.percent())

	await transferRemaining(input, session, session.remaining, progress, callbacks)

	callbacks.onConfirming()
	const file = await finalizeWithRepair(input, session, progress, callbacks)
	forgetSession(input.file)
	return file
}

/** One transfer pass over the given parts, with cancellation converted into an abort of the whole session. */
async function transferRemaining(
	input: PreparedUploadInput,
	session: ResolvedMultipartSession,
	parts: number[],
	progress: PartProgress,
	callbacks: UploaderCallbacks,
): Promise<void> {
	if (parts.length === 0) return
	const plan = new Map(
		planParts(session.declaredFileSize, session.partSize, session.partCount).map((p) => [p.partNumber, p]),
	)
	const urls = buildUrlProvider(input.fileId, input.file, session)

	try {
		await transferParts(parts, {
			file: input.file,
			plan,
			urls,
			uploadPart: fileMultipartService.uploadPart,
			progress,
			onProgress: callbacks.onProgress,
			signal: input.signal,
		})
	} catch (error) {
		await handleTransferFailure(input, session, error)
	}
}

/**
 * Seeds the URLs the control plane already returned, then requests further
 * batches only as the scheduler needs them. A `503` on the presign window is a
 * bounded, retryable control-plane condition: it pauses this session rather
 * than failing or recreating it.
 */
function buildUrlProvider(fileId: string, file: File, session: ResolvedMultipartSession): PartUrlProvider {
	const requestBatch = async (partNumbers: number[]) => {
		const response = await fileMultipartService.createOrPresign(fileId, {
			multipartSessionId: session.multipartSessionId,
			partNumbers,
		})
		return response.uploadParts
	}
	const provider = new PartUrlProvider(async (partNumbers) => {
		try {
			return await requestPresignWithBackoff(requestBatch, partNumbers)
		} catch (error) {
			if (multipartErrorCode(error) === 'PRESIGN_CREDENTIAL_WINDOW_TOO_SHORT') {
				// The session is untouched and still holds every transferred part, so
				// the offer is Retry against that same session, or Cancel to release
				// it — never a new attempt.
				throw new MultipartUploadError('Upload paused. Please try again in a moment.', MultipartFailureKind.RETRYABLE, {
					code: 'PRESIGN_CREDENTIAL_WINDOW_TOO_SHORT',
					multipartSessionId: session.multipartSessionId,
					actions: [UploadRecoveryAction.RETRY, UploadRecoveryAction.CANCEL],
					releaseSession: releaseSessionFn(fileId, session.multipartSessionId, file),
				})
			}
			throw error
		}
	})
	for (const seed of session.seeded) {
		provider.seed(seed)
	}
	return provider
}

/**
 * Cancellation aborts the session in Atlas before this upload gives up; a
 * vanished multipart stops part scheduling entirely and reconciles through
 * `GET` rather than re-presigning into a loop.
 */
async function handleTransferFailure(
	input: PreparedUploadInput,
	session: ResolvedMultipartSession,
	error: unknown,
): Promise<never> {
	if (error instanceof MultipartUploadGoneError) {
		forgetSession(input.file)
		throw new MultipartUploadError(
			'This upload can no longer be resumed. Please start over.',
			MultipartFailureKind.RESTART_REQUIRED,
			{
				multipartSessionId: session.multipartSessionId,
				resumeCleared: true,
				actions: [UploadRecoveryAction.START_OVER],
			},
		)
	}
	if (error instanceof MultipartUploadError && error.kind === MultipartFailureKind.CANCELLED) {
		await fileMultipartService
			.abort(input.fileId, session.multipartSessionId)
			.catch((abortError: unknown) => log.warn('Abort after cancellation did not converge', { abortError }))
		forgetSession(input.file)
	}
	throw error
}

/**
 * Finalizes, repairing the exact parts a rejected part set names.
 *
 * The repair is bounded: an already-issued `UploadPart` can land after the
 * pre-claim check, so one answer of "these parts are wrong" is worth acting on,
 * and an endless repair loop is not.
 */
async function finalizeWithRepair(
	input: PreparedUploadInput,
	session: ResolvedMultipartSession,
	progress: PartProgress,
	callbacks: UploaderCallbacks,
): Promise<FileDTO> {
	for (let round = 1; ; round++) {
		try {
			return await finalizeSession({ fileId: input.fileId, sessionId: session.multipartSessionId })
		} catch (error) {
			const repair = round < PART_SET_REPAIR_ROUNDS ? partsToRepair(error) : []
			if (repair.length === 0) throw error
			log.info('Repairing parts rejected at completion', { parts: repair })
			for (const partNumber of repair) progress.invalidate(partNumber)
			callbacks.onProgress(progress.percent())
			await transferRemaining(input, session, repair, progress, callbacks)
		}
	}
}

/** The union of missing and wrong-sized parts a `MULTIPART_PART_SET_INVALID` response names. */
function partsToRepair(error: unknown): number[] {
	if (multipartErrorCode(error) !== 'MULTIPART_PART_SET_INVALID') return []
	const details = multipartErrorDetails(error)
	const missing = Array.isArray(details.missingPartNumbers) ? (details.missingPartNumbers as number[]) : []
	const invalid = Array.isArray(details.invalidParts)
		? (details.invalidParts as { partNumber: number }[]).map((part) => part.partNumber)
		: []
	return [...new Set([...missing, ...invalid])].sort((a, b) => a - b)
}
